import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { MapPin } from 'lucide-react';
import geoapifyService from '../../../../services/geoapifyService';
import debounce from 'lodash.debounce';
import Loading from '../../../loading-animation';
import './AddressForm.scss';

const AddressForm = ({ 
  title, 
  prefix = '', // 'pickup' or 'delivery'
  required = false 
}) => {
  const { register, formState: { errors }, setValue, watch } = useFormContext();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchZipCode, setSearchZipCode] = useState(''); // Separate state for search input
  const suggestionsRef = useRef(null);
  const searchInputRef = useRef(null);

  // For pickup/delivery, address is nested: pickup.address.address, pickup.address.city, etc.
  const getFieldName = (field) => {
    if (prefix && (field === 'address' || field === 'city' || field === 'state' || field === 'zipCode')) {
      return `${prefix}.address.${field}`;
    }
    return prefix ? `${prefix}.${field}` : field;
  };

  // Debounced search function
  const searchAddresses = React.useMemo(
    () => debounce(async (zipCodeValue) => {
      if (!zipCodeValue || zipCodeValue.trim().length < 3) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await geoapifyService.searchByZipCode(zipCodeValue.trim());
        setAddressSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        console.error('[AddressForm] Error searching addresses:', error);
        setAddressSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 500), // 500ms debounce
    []
  );

  // Search when search ZIP code changes
  useEffect(() => {
    if (searchZipCode && searchZipCode.trim().length >= 3) {
      searchAddresses(searchZipCode);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }

    // Cleanup debounce on unmount
    return () => {
      searchAddresses.cancel();
    };
  }, [searchZipCode, searchAddresses]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle address selection
  const handleSelectAddress = (address) => {
    setValue(getFieldName('address'), address.address || '', { shouldDirty: true });
    setValue(getFieldName('city'), address.city || '', { shouldDirty: true });
    setValue(getFieldName('state'), address.state || '', { shouldDirty: true });
    setValue(getFieldName('zipCode'), address.zipCode || searchZipCode, { shouldDirty: true });
    // Clear search input and suggestions after selection
    setSearchZipCode('');
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  return (
    <div className="address-form">
      <h3 className="form-section-title">{title}</h3>
      
      <div className="form-grid">
        {/* Address Search Input - Separate field for searching addresses */}
        <div className="form-group" style={{ position: 'relative', gridColumn: '1 / -1' }}>
          <label htmlFor={`${prefix}-address-search`}>
            Search Address by ZIP Code
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              id={`${prefix}-address-search`}
              placeholder="Enter ZIP code to search addresses"
              value={searchZipCode}
              onChange={(e) => {
                const value = e.target.value;
                setSearchZipCode(value);
              }}
              ref={searchInputRef}
              onFocus={() => {
                if (addressSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              style={{
                padding: '12px 16px',
                border: '1px solid var(--theme-border)',
                borderRadius: '8px',
                fontSize: '14px',
                width: '100%'
              }}
            />
            {isLoading && (
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                <Loading size="small" inline />
              </div>
            )}
          </div>
          
          {/* Address Suggestions Dropdown */}
          {showSuggestions && addressSuggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="address-suggestions-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: '300px',
                overflowY: 'auto',
                marginTop: '4px'
              }}
            >
              {addressSuggestions.map((address, index) => {
                // Format address display similar to screenshot
                // Extract location name (city/municipality) and state
                const locationName = address.city || address.raw?.city || address.raw?.municipality || '';
                const stateName = address.state || address.raw?.state || '';
                const countryName = address.raw?.country || 'United States';
                
                // Build formatted address: "Location Name, State, Country"
                const formattedLocation = [
                  locationName,
                  stateName,
                  countryName
                ].filter(Boolean).join(', ');
                
                return (
                  <div
                    key={index}
                    onClick={() => handleSelectAddress(address)}
                    className="address-suggestion-item"
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: index < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {/* Map Pin Icon */}
                    <div style={{ 
                      flexShrink: 0, 
                      marginTop: '2px',
                      color: '#e91e63' // Pink color like in screenshot
                    }}>
                      <MapPin size={16} />
                    </div>
                    
                    {/* Address Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '14px',
                        color: '#212529',
                        lineHeight: '1.4'
                      }}>
                        {/* ZIP Code in bold, followed by formatted address */}
                        <span style={{ fontWeight: '700', marginRight: '4px' }}>
                          {address.zipCode}
                        </span>
                        <span style={{ fontWeight: '400' }}>
                          {formattedLocation}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={getFieldName('locationName')}>
            Location Name
          </label>
          <input
            type="text"
            id={getFieldName('locationName')}
            placeholder="Text"
            {...register(getFieldName('locationName'))}
            className={errors[getFieldName('locationName')] ? 'error' : ''}
          />
          {errors[getFieldName('locationName')] && (
            <span className="error-message">
              {errors[getFieldName('locationName')].message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={getFieldName('address')}>
            Address
          </label>
          <input
            type="text"
            id={getFieldName('address')}
            placeholder="Text"
            {...register(getFieldName('address'))}
            className={errors[prefix]?.address?.address ? 'error' : ''}
          />
          {errors[prefix]?.address?.address && (
            <span className="error-message">
              {errors[prefix].address.address.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={getFieldName('city')}>
            City {required && '*'}
          </label>
          <input
            type="text"
            id={getFieldName('city')}
            placeholder="Text"
            {...register(getFieldName('city'), {
              required: required ? 'City is required' : false
            })}
            className={errors[prefix]?.address?.city ? 'error' : ''}
          />
          {errors[prefix]?.address?.city && (
            <span className="error-message">
              {errors[prefix].address.city.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={getFieldName('state')}>
            State {required && '*'}
          </label>
          <input
            type="text"
            id={getFieldName('state')}
            placeholder="Text"
            {...register(getFieldName('state'), {
              required: required ? 'State is required' : false
            })}
            className={errors[prefix]?.address?.state ? 'error' : ''}
          />
          {errors[prefix]?.address?.state && (
            <span className="error-message">
              {errors[prefix].address.state.message}
            </span>
          )}
        </div>

        {/* ZIP Code Input - Actual form field that gets saved */}
        <div className="form-group">
          <label htmlFor={getFieldName('zipCode')}>
            ZIP Code
          </label>
          <input
            type="text"
            id={getFieldName('zipCode')}
            placeholder="Text"
            {...register(getFieldName('zipCode'))}
            className={errors[prefix]?.address?.zipCode ? 'error' : ''}
          />
          {errors[prefix]?.address?.zipCode && (
            <span className="error-message">
              {errors[prefix].address.zipCode.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={getFieldName('contactPhone')}>
            Contact Phone
          </label>
          <input
            type="text"
            id={getFieldName('contactPhone')}
            placeholder="Text"
            {...register(getFieldName('contactPhone'))}
            className={errors[getFieldName('contactPhone')] ? 'error' : ''}
          />
          {errors[getFieldName('contactPhone')] && (
            <span className="error-message">
              {errors[getFieldName('contactPhone')].message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
